/* eslint complexity: [1, 40] */
/**
 * Initialises functionality that calculates if banner should be shown or hidden on scroll. Note that the actual showing/hiding
 * happens in calling classes. This is so that the showing/hiding of the banner and of the top row of nav can be done
 * by changing the style of a single element, which improves performance of the header on Safari.
 * @return {object} Object with functions to show/hide banner, enable banner, check if banner should be contracted, and add a listener
 * to actually perform show/hide, as well as a pointer to the banner itself.
 */
const initBannerHide = () => {
  if (!window.cmhrBetaBannerSettings) {
    return;
  }
  const banner = window.cmhrBetaBannerSettings.getBanner();
  let is_disabled = false;
  let threshold = 0;
  updateThreshold();
  const listeners = [];

  function getThresholdBase() {
    return window.curtainSettings && window.curtainSettings.curtainEl
      ? window.innerHeight
      : 0;
  }

  function updateThreshold() {
    threshold = getThresholdBase() + window.cmhrBetaBannerSettings.getCurrentSize('beta-banner-height-init') * 1.25;
  }
  /**
   * Disable the banner movement.
   */
  function disableBanner() {
    is_disabled = true;
  }

  /**
   * Enable the banner movement.
   */
  function enableBanner() {
    is_disabled = false;
  }

  if (banner) {
    let is_contracted = false;

    function runListeners() {
      listeners.forEach((listener) => listener(is_contracted));
    }
    /**
     * Initializes banner hiding.
     */
    function hideBanner() {
      if (!is_disabled) {
        is_contracted = true;
        document.body.classList.add("is-banner-contracted");
        runListeners();
      }
    }

    function showBanner() {
      is_contracted = false;
      document.body.classList.remove("is-banner-contracted");
      runListeners();
    }

    /**
     * Scroll/run handler.
     */
    function runBanner() {
      requestAnimationFrame(() => {
        if (!is_disabled) {
          let pos =
            window.scrollY ||
            window.pageYOffset ||
            document.documentElement.scrollTop;
          if (
            pos > threshold &&
            !is_contracted
          ) {
            hideBanner();
          } else if (pos < threshold && is_contracted) {
            showBanner();
          }
        }
      });
    }

    window.addEventListener("scroll", runBanner, { passive: true });

    window.cmhrBetaBannerSettings.registerResize(banner.id, updateThreshold);

    Array.from(document.querySelectorAll(".js-beta-banner a")).forEach((el) => {
      el.addEventListener("focus", showIfContracted);
    });

    function showIfContracted() {
      if (isContracted()) {
        showBanner();
      }
    }
    function isContracted() {
      return is_contracted;
    }

    function addContractedChangeListener(listener) {
      listeners.push(listener);
    }

    return {
      hideBanner,
      runBanner,
      disableBanner,
      enableBanner,
      banner,
      isContracted,
      addContractedChangeListener,
    };
  } else {
    return { hideBanner: () => {}, runBanner: () => {} };
  }
};

export default initBannerHide;
